<script setup>
import { ref, onMounted, nextTick } from 'vue'
import DocumentEditor from '@/components/Editor/DocumentEditor.vue'

const STORAGE_KEY = 'webscout-scratchpad-content'

const emit = defineEmits(['change'])

const editorRef = ref(null)

// Load from localStorage
function loadFromStorage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch {
    // Ignore parse errors
  }
  return { blocks: [] }
}

// Save to localStorage
function saveToStorage(content) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(content))
  } catch {
    // Ignore storage errors
  }
}

// Initialize with stored content
const initialData = ref(loadFromStorage())

// Debounce timer
let saveTimeout = null

// Handle content changes with debounced autosave
async function handleChange() {
  emit('change')

  // Debounced save (50ms)
  if (saveTimeout) {
    clearTimeout(saveTimeout)
  }
  saveTimeout = setTimeout(async () => {
    if (editorRef.value) {
      const content = await editorRef.value.save()
      saveToStorage(content)
    }
  }, 50)
}

// Handle save shortcut (immediate save)
async function handleSave() {
  if (editorRef.value) {
    // Clear any pending debounced save
    if (saveTimeout) {
      clearTimeout(saveTimeout)
      saveTimeout = null
    }
    const content = await editorRef.value.save()
    saveToStorage(content)
  }
}

// Auto-focus editor at end when opened
onMounted(async () => {
  await nextTick()
  // Small delay to ensure EditorJS is fully initialized
  setTimeout(async () => {
    if (editorRef.value) {
      await editorRef.value.focus()
    }
  }, 100)
})
</script>

<template>
  <DocumentEditor
    ref="editorRef"
    :initial-data="initialData"
    placeholder="Write your investigation notes..."
    @change="handleChange"
    @save="handleSave"
  />
</template>
